import React from "react";
import Nav from "../components/nav";
import Footer from "../components/footer";
import { skillCategories } from "../data/cart";
import { Link } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import AnimatedSection from "../components/AnimatedSection";

function Skills() {
  usePageMeta("/skills");

  return (
    <>
      <Nav />
      <main>
      
      {/* Hero Section */}
      <section className="bg-textColor pt-24 pb-20">
        <div className="container mx-auto px-8">
          <AnimatedSection animation="fade-down" className="text-center mb-8">
            <div className="kicker">Skills</div>
            <h1 className="display-title text-4xl md:text-5xl font-bold text-primary mb-4">Technical Skills</h1>
            <div className="w-24 h-1 bg-secondary mx-auto rounded-full"></div>
            <p className="text-xl text-gray-600 mt-6 max-w-2xl mx-auto">
              Crawlable overview of my frontend, backend, database, UI/UX, and development-tool skills.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Skills Section */}
      <section className="bg-ambient py-12" aria-label="Technical skill categories">
        <div className="container mx-auto px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {skillCategories.map((category, index) => (
              <AnimatedSection key={index} animation="fade-up" delay={index * 120}>
                <div className="card-surface p-8 rounded-2xl hover:scale-105 transition-transform">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-primary bg-opacity-10 rounded-full flex items-center justify-center mr-4">
                      <div className="w-6 h-6 bg-primary rounded-full"></div>
                    </div>
                    <h3 className="text-xl font-bold text-primary">{category.title}</h3>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {category.skills.map((skill, i) => (
                      <span 
                        key={i} 
                        className="skill-tag-shimmer px-4 py-2 text-sm bg-secondary bg-opacity-10 text-primary border border-secondary border-opacity-30 rounded-lg font-medium hover:bg-secondary hover:text-textColor transition-all duration-200"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="bg-textColor py-16" aria-labelledby="learning-heading">
        <div className="container mx-auto px-8">
          <AnimatedSection animation="fade-up" className="text-center mb-12">
            <div className="kicker">Learning</div>
            <h2 id="learning-heading" className="display-title text-3xl md:text-4xl font-bold text-primary mb-4">Certifications & Learning Goals</h2>
            <div className="w-20 h-1 bg-secondary mx-auto rounded-full"></div>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { title: "LinkedIn Learning: Java OOP", desc: "Object-Oriented Programming Fundamentals", status: "Completed", color: "green" },
              { title: "React & MERN Stack", desc: "Full-Stack Web Development", status: "Completed", color: "green" },
              { title: "Web Design with Tailwind CSS", desc: "Responsive UI/UX Design", status: "Completed", color: "green" },
     ].map((cert, index) => (
              <AnimatedSection key={index} animation="scale-up" delay={index * 150}>
                <div className="card-surface p-8 rounded-2xl hover:scale-105 transition-transform text-center">
                  <div className={`w-16 h-16 bg-${cert.color}-100 rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <svg className={`w-8 h-8 text-${cert.color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={cert.color === "green" ? "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" : cert.color === "blue" ? "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" : "M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"} />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-primary mb-2">{cert.title}</h3>
                  <p className="text-gray-600 mb-4">{cert.desc}</p>
                  <span className={`bg-${cert.color}-100 text-${cert.color}-700 px-4 py-2 rounded-full text-sm font-medium`}>
                    {cert.status}
                  </span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-secondary p-6">
        <div className="container mx-auto px-8 text-center">
          <AnimatedSection animation="fade-up" className="max-w-3xl mx-auto">
            <h2 className="display-title text-3xl md:text-4xl font-bold mb-4 text-primary">Let's Build Something Amazing</h2>
            <p className="text-lg md:text-xl mb-8 text-primary">
              Ready to collaborate on your next project? Let's discuss how my skills can help bring your ideas to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/projects" className="bg-primary text-textColor font-bold py-3 px-8 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg w-full sm:w-auto">
                  View My Projects
              </Link>
              <Link to="/contact" className="bg-primary text-textColor font-bold py-3 px-8 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg w-full sm:w-auto">
                  Contact Me
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
      </main>

      <Footer />
    </>
  );
}

export default Skills;
